import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { select, Store } from '@ngrx/store';

import { each, has } from 'lodash-es';
import { Observable } from 'rxjs';

import { GenericComponent } from '../../misc/components/generic/generic.component';
import * as fromSettingActions from '../../store/actions/setting.actions';
import * as fromDataProviderSelectors from '../../store/selectors/data-provider.selectors';
import * as fromSettingsSelectors from '../../store/selectors/setting.selectors';
import * as fromSettingSelectors from '../../store/selectors/setting.selectors';
import { FormValidationErrors } from '../../typings/form-validation-errors';

interface LoginAccountFormGroup {
  password: FormControl<string | null>;
  username: FormControl<string | null>;
}
interface ReferralCodeFormGroup {
  code: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-login-credentials',
  standalone: false,
  templateUrl: './login-credentials.page.html'
})
export class LoginCredentialsPageComponent extends GenericComponent {
  private store = inject(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);

  loginForm: FormGroup<LoginAccountFormGroup> = new FormGroup<LoginAccountFormGroup>({
    password: new FormControl<string>('', Validators.required),
    username: new FormControl<string>('', Validators.required)
  });
  referralForm: FormGroup<ReferralCodeFormGroup> = new FormGroup<ReferralCodeFormGroup>({
    code: new FormControl<string>('', Validators.required)
  });
  loginFormValidationErrors: FormValidationErrors = {};
  referralFormValidationErrors: FormValidationErrors = {};

  isBlackKnight$: Observable<boolean>;
  debugMode$: Observable<boolean>;

  constructor() {
    super();

    this.isBlackKnight$ = this.store.pipe(select(fromDataProviderSelectors.selectIsBlackKnight));
    this.debugMode$ = this.store.pipe(select(fromSettingSelectors.selectIsDebugModeEnabled));
  }

  ionViewDidEnter() {
    this.addUniqueSubscription(
      'selectDataProviderCredentials',
      this.store
        .pipe(select(fromDataProviderSelectors.selectDataProviderCredentials))
        .subscribe((dataProviderCredentials) => {
          this.loginForm.setValue({
            password: dataProviderCredentials.password ? atob(dataProviderCredentials.password) : null,
            username: dataProviderCredentials.username
          });
          this.loginForm.markAsPristine();
          this.loginForm.updateValueAndValidity();
          this.changeDetectorRef.detectChanges();
        })
    );

    this.addUniqueSubscription(
      'selectReferralCode',
      this.store.pipe(select(fromSettingsSelectors.selectReferralCode)).subscribe((code) => {
        this.referralForm.setValue({
          code
        });
        this.referralForm.markAsPristine();
        this.referralForm.updateValueAndValidity();
        this.changeDetectorRef.detectChanges();
      })
    );

    this.addUniqueSubscription(
      'selectLoginFormValidationErrors',
      this.store
        .pipe(select(fromDataProviderSelectors.selectLoginFormValidationErrors))
        .subscribe((formValidationErrors) => {
          this.loginFormValidationErrors = formValidationErrors;
          const controlNames = ['username', 'password'];
          each(controlNames, (controlName) => {
            const control = this.loginForm.get(controlName);
            if (has(this.loginFormValidationErrors, controlName)) {
              control?.setErrors(this.loginFormValidationErrors[controlName]);
            } else {
              control?.setErrors(null);
            }
          });
          this.changeDetectorRef.detectChanges();
        })
    );

    this.addUniqueSubscription(
      'selectReferralFormValidationErrors',
      this.store
        .pipe(select(fromSettingsSelectors.selectReferralFormValidationErrors))
        .subscribe((formValidationErrors) => {
          this.referralFormValidationErrors = formValidationErrors;
          const controlNames = ['code'];
          each(controlNames, (controlName) => {
            const control = this.referralForm.get(controlName);
            if (has(this.referralFormValidationErrors, controlName)) {
              control?.setErrors(this.referralFormValidationErrors[controlName]);
            } else {
              control?.setErrors(null);
            }
          });
          this.changeDetectorRef.detectChanges();
        })
    );
  }

  onSubmit() {
    this.store.dispatch(
      fromSettingActions.updateReferralCode({
        payload: {
          code: this.referralForm.get('code')?.value || '',
          password: this.loginForm.get('password')?.value ? btoa(this.loginForm.get('password')?.value || '') : null,
          username: this.loginForm.get('username')?.value || null
        }
      })
    );
  }
}
